<template>
  <div class="page-header pb-container">
    <div class='size1-text pri-color'>Past Billing Cycle {{ fmtCycleDate }}</div>

    <div class="columns">
        <div class="column header-labels">
          <div class="page-heading-label">Total Billing</div>
          <div class="page-heading-label">Total Received</div>
          <div class="page-heading-label">Total Outstanding</div>
        </div>
        <div class="column">
          <div class="page-heading-total-amount app-green">{{ totalBillingAmtFormatted }}</div>
          <div class="page-heading-total-amount app-green">{{ totalReceivedAmtFormatted }}</div>
          <div class="page-heading-total-amount app-red">{{ totalOutstandingAmtFormatted }}</div>
        </div>
    </div>
  </div>
  <router-link to="/past-billings" class="pri-color breadcrumb">
    &lt; Back to Past Billing Cycles
  </router-link>

  <div class="level">
    <div class="level-left">
      <div class="page-heading-label level-item">
          Per Page:
      </div>
      <div class="level-item">
        <select v-on:change="onPageSizeChanged()" id="page-size">
            <option value="10" selected="">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="200">200</option>
        </select>
      </div>
    </div>
    <div class="level-right">
      <div class="level-item">
        <button id="export-button" v-on:click="onBtExport()" class="button is-secondary">
          Export to CSV
        </button>
      </div>
    </div>
  </div>
  <ag-grid-vue
      style="width: 100%; height: 600px;"
      class="ag-theme-alpine"
      id="myGrid"
      :gridOptions="gridOptions"
      @grid-ready="onGridReady"
      :columnDefs="columnDefs"
      :pagination="true"
      :paginationPageSize="paginationPageSize"
      :defaultColDef="defaultColDef"
      :modules="modules"
      :rowData="rowData">
  </ag-grid-vue>
  <div id='injectable-tag-wrapper' style="display:none;"></div>
  <PageLoading :showLoading="!isReady"></PageLoading>
</template>

<script>
import { format } from 'date-fns';
import { AgGridVue } from 'ag-grid-vue3';
import { AllCommunityModules } from 'ag-grid-community';
import MoneyUtils from '@/shared/money-utils';
import PageLoading from '@/components/page-loading.vue';
import GridUtils from '@/shared/grid-utils';

window.currencyFormatter = function currencyFormatter(params) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  return formatter.format(params.value);
};

export default {
  name: 'CurrentBilling',
  components: { AgGridVue, PageLoading },
  data() {
    return {
      billRunPaused: false,
      gridOptions: null,
      gridApi: null,
      columnApi: null,
      columnDefs: null,
      defaultColDef: null,
      modules: AllCommunityModules,
      rowData: null,
      paginationPageSize: null,
      totalReceivedAmt: 0,
      totalBillingAmt: 0,
      cycleDate: '',
      isReady: false,
    };
  },
  mixins: [MoneyUtils, GridUtils],
  methods: {
    async fetchGridData() {
      this.isReady = false;
      await this.$store.dispatch('billCycles/fetchPastBillCycle', { id: this.id });
      this.rowData = this.$store.state.billCycles.pastBillCycle;
      this.computeTotalsDue();
      this.isReady = true;
    },
    computeTotalsDue() {
      if (!this.rowData || this.rowData.length === 0) {
        this.rowData = [];
      }
      this.rowData.forEach((row) => {
        this.totalBillingAmt += row.GrandTotalDue;
        if (row.IsPaid === 1) {
          this.totalReceivedAmt += row.GrandTotalDue;
        }
      });
      this.cycleDate = this.rowData[0].BillToDate;
    },
    async onGridReady() {
      // eslint-disable-next-line no-console
      await this.fetchGridData();
    },
    onBtExport() {
      const expParams = {
        columnKeys: ['BusinessName', 'ContactName', 'TotalTR', 'VinCorTR', 'BillableTR', 'UnitCost', 'CurrentTotal', 'PastDueTotal', 'AdjustmentAmount', 'GrandTotalDue', 'Status', 'Docs'],
      };

      this.gridApi.exportDataAsCsv(expParams);
      // unfortunately, you need a commercial license of ag grid for this one.
      //      this.gridApi.exportDataAsExcel();
    },
    toggleBilRunStatus() {
      this.billRunPaused = !this.billRunPaused;
    },
    onPageSizeChanged() {
      const ps = document.getElementById('page-size').value;
      this.gridApi.paginationSetPageSize(Number(ps));
    },
    buildArrayRowForCSV(rawData) {
      console.log('inside build array row for CSV');
      const res = [];
      res.push(rawData.Idx);
      res.push(rawData.UserId);
      res.push(rawData.BusinessName);
      res.push(rawData.Contact);
      res.push(`"=""${rawData.EIN}"""`);
      res.push(rawData.Email);
      res.push(`"=""${rawData.SubmissionId}"""`);
      res.push(`"=""${rawData.ReturnId}"""`);
      res.push(rawData.CorrectedVIN);
      res.push(rawData.UnitCost);
      res.push(rawData.Declined);
      res.push(rawData.BillingDate);
      res.push(rawData.FilingDate);

      return res;
    },
  },
  beforeMount() {
    this.gridOptions = {};
    this.columnDefs = [
      {
        field: 'InvoiceNumber',
        headerName: 'Inv/Rec #',
        filter: 'agTextColumnFilter',
        filterParams: GridUtils.textFilterParams,
        minWidth: 125,
        maxWidth: 140,
        editable: true,
      },
      {
        field: 'BusinessName',
        filter: 'agTextColumnFilter',
        filterParams: GridUtils.textFilterParams,
        minWidth: 180,
        editable: true,
      },
      {
        field: 'ContactName',
        headerName: 'Contact',
        filter: 'agTextColumnFilter',
        filterParams: GridUtils.textFilterParams,
        editable: true,
      },
      {
        field: 'TotalTR',
        headerName: 'Tot Ret',
        editable: true,
      },
      {
        field: 'VinCorTR',
        headerName: 'VIN Cor TR',
        editable: true,
      },
      {
        field: 'BillableTR',
        headerName: 'Billed #',
        editable: true,
      },
      {
        field: 'UnitCost',
        editable: true,
        // eslint-disable-next-line no-undef
        valueFormatter: currencyFormatter,
      },
      {
        field: 'CurrentTotal',
        headerName: 'Current Amt',
        editable: true,
        // eslint-disable-next-line no-undef
        valueFormatter: currencyFormatter,
      },
      {
        field: 'PastDueTotal',
        headerName: 'Past Due Amt',
        editable: true,
        // eslint-disable-next-line no-undef
        valueFormatter: currencyFormatter,
      },
      {
        field: 'AdjustmentAmount',
        headerName: 'Adj Amt',
        editable: true,
        // eslint-disable-next-line no-undef
        valueFormatter: currencyFormatter,
      },
      {
        field: 'GrandTotalDue',
        headerName: 'Grand Total',
        editable: true,
        minWidth: 100,
        // eslint-disable-next-line no-undef
        valueFormatter: currencyFormatter,
      },
      {
        field: 'Status',
        headerName: 'Status',
        filter: 'agTextColumnFilter',
        filterParams: GridUtils.textFilterParams,
        editable: true,
      },
      {
        field: 'Docs',
        headerName: 'Docs',
        filter: 'agTextColumnFilter',
        filterParams: GridUtils.textFilterParams,
        editable: true,
      },
      {
        field: 'UserID',
        headerName: '',
        sortable: false,
        filter: false,
        minWidth: 190,
        cellRenderer: (params) => {
          const wrap = document.createElement('div');

          const sep = document.createElement('span');
          sep.innerText = ' | ';

          const route = {
            name: 'customer-detail',
            params: { id: params.value, returnTo: `past-billings/${this.id}` },
          };
          const detailsLink = document.createElement('a');
          detailsLink.href = this.$router.resolve(route).href;
          detailsLink.innerText = 'View Details';
          detailsLink.addEventListener('click', (e) => {
            e.preventDefault();
            this.$router.push(route);
          });

          const exportLink = document.createElement('a');
          // exportLink.setAttribute('id', `billStatus-${params.data.UserID}`);
          exportLink.href = '#';
          // console.log('Params are: ');
          // console.log(params.data.status);
          exportLink.innerText = 'Export';
          exportLink.addEventListener('click', async (e) => {
            e.preventDefault();
            await this.$store.dispatch('billCycles/fetchPastBillCycleReturns', { id: this.id, userId: params.data.UserID });
            const rows = this.$store.state.billCycles.pastBillCycleReturns;
            const formattedRows = rows.map((el) => this.buildArrayRowForCSV(el));
            const headerRow = ['Idx', 'UserId', 'BusinessName', 'Contact', 'EIN', 'Email', 'SubmissionId', 'ReturnId', 'CorrectedVIN', 'UnitCost', 'Declined', 'BillingDate', 'FilingDate'];
            formattedRows.unshift(headerRow);

            const csvContent = `data:text/csv;charset=utf-8,${formattedRows.map((el) => el.join(',')).join('\n')}`;
            const fileName = `PastBillingReturnData-${params.data.UserID}-${this.id}.csv`;

            const encodedUri = encodeURI(csvContent);
            const tempExportLink = document.createElement('a');
            tempExportLink.setAttribute('href', encodedUri);
            tempExportLink.setAttribute('download', fileName);
            const linkWrap = document.getElementById('injectable-tag-wrapper');
            linkWrap.innerHTML = tempExportLink; // Required for FF
            tempExportLink.click(); // This will download the data file
          });
          wrap.appendChild(exportLink);
          wrap.appendChild(sep);
          wrap.appendChild(detailsLink);
          return wrap;
        },
      },
    ];
    this.defaultColDef = {
      editable: false,
      sortable: true,
      flex: 1,
      minWidth: 80,
      filter: 'agNumberColumnFilter',
      filterParams: GridUtils.numberFilterParams,
      resizable: true,
    };
    this.paginationPageSize = 10;
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
  },
  computed: {
    visibleProducts() {
      return this.$store.getters.filteredProducts;
    },
    fmtCycleDate() {
      if (!this.cycleDate || this.cycleDate.length < 8) {
        return '';
      }
      // never let the date constructor parse the full date string
      // it behaves differently between browsers.  I parsed out
      // the individual fields instead.
      const dateParts = this.cycleDate.substring(0, 10).split('-');
      const cycDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
      return format(cycDate, '(MM/dd/yy)');
    },
    totalOutstandingAmtFormatted() {
      return this.formatAsMoney(this.totalBillingAmt - this.totalReceivedAmt);
    },
    totalReceivedAmtFormatted() {
      return this.formatAsMoney(this.totalReceivedAmt);
    },
    totalBillingAmtFormatted() {
      return this.formatAsMoney(this.totalBillingAmt);
    },
  },
  props: {
    id: Number,
  },
};

</script>
<style src='ag-grid-community/dist/styles/ag-theme-alpine.css'></style>
<style src='ag-grid-community/dist/styles/ag-grid.css'></style>
<style lang="scss" scoped>
    #export-button {
        font-weight: bold;
    }

@import "~ag-grid-community/src/styles/ag-grid.scss";
@import "~ag-grid-community/src/styles/ag-theme-alpine/sass/ag-theme-alpine-mixin";
.ag-theme-alpine {
    @include ag-theme-alpine((
        // use theme parameters where possible
        header-foreground-color: #FFF,
        header-background-color: #0F3D80,
        font-family: ('Helvetica', 'Arial', 'sans-serif'),
    ));

    .ag-icon {
      color: #FFF;
    }
    .ag-paging-button > .ag-icon {
        color: #000;
    }
    .ag-header-cell-text {
      font-size: 14px;
    }
}
  .pb-container {
    display: flex;
    justify-content: space-between;
  }
  .tb {
    padding-left: 40px;
  }
  .tr {
    padding-left: 20px;
  }
  .pause-btn-toggle {
    min-width: 153px;
  }

</style>
